import { useEffect, useRef, useState } from "react";

export const STAGE_WIDTH = 1920;
export const STAGE_HEIGHT = 1080;

/**
 * A fixed 1920×1080 canvas scaled to fit, letterboxed on the cream
 * background. Laying the display out once at 1080p and scaling it means
 * a 720p TV, a 4K TV and a laptop on a projector all show exactly the
 * same slides, so each slide can hold a known number of rows.
 *
 * `fit="screen"` (the display) fills the window and hides the cursor;
 * `fit="container"` (the settings preview) is a 16:9 box as wide as its
 * parent.
 */
export default function TvStage({ fit = "screen", children }) {
  const frame = useRef(null);
  const [scale, setScale] = useState(fit === "screen" ? 1 : 0);

  useEffect(() => {
    const el = frame.current;
    const measure = () => setScale(Math.min(el.clientWidth / STAGE_WIDTH, el.clientHeight / STAGE_HEIGHT));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const frameClass =
    fit === "screen"
      ? "fixed inset-0 z-50 cursor-none overflow-hidden bg-background"
      : "relative aspect-video w-full overflow-hidden rounded-xl border border-border bg-background";

  return (
    <div ref={frame} className={frameClass}>
      <div
        className="absolute left-1/2 top-1/2 overflow-hidden"
        style={{
          width: STAGE_WIDTH,
          height: STAGE_HEIGHT,
          transform: `translate(-50%, -50%) scale(${scale})`,
        }}
      >
        {children}
      </div>
    </div>
  );
}
